import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar'; // Assuming you have this from previous steps
import ProductCard from '../components/ProductCard';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Order, Product } from '../types';
import { fetchUserOrders, fetchProducts, updateUserProfile } from './index';
import axios from 'axios';

const UserDashboard = () => {
  const { user, logout, login } = useAuth(); // login used to update context on profile save
  const { cart, removeFromCart } = useCart();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    address: user?.address || ''
  });

  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewAllOrders, setViewAllOrders] = useState(false);

  // Sync profileData with user context when it loads or updates
  useEffect(() => {
    if (user) {
      setProfileData(prev => ({
        ...prev,
        name: user.name || prev.name,
        phone: user.phone || prev.phone,
        address: user.address || prev.address
      }));
    }
  }, [user]);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        // Fetch latest profile data to ensure context is up to date
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const { data } = await axios.get('/api/users/profile', {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (data) {
              const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
              const updatedUser = { ...currentUser, ...data };
              localStorage.setItem('user', JSON.stringify(updatedUser));
              login(updatedUser);
            }
          }
        } catch (err) {
          console.warn("Failed to fetch latest profile", err);
        }

        const [ordersRes, productsRes] = await Promise.all([
          fetchUserOrders(),
          fetchProducts()
        ]);
        setOrders(ordersRes.data.map((o: any) => ({ ...o, id: o.id || o._id })));
        setProducts(productsRes.data.map((p: any) => ({ ...p, id: p._id })));
      } catch (error) {
        console.error("Failed to load dashboard data", error);
      } finally {
        setLoading(false);
      }
    };
    loadDashboardData();
  }, [login]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSaveProfile = async () => {
    try {
      const { data } = await updateUserProfile(profileData);
      // Update local storage and context
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedUser = { ...currentUser, ...data };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      login(updatedUser); // Refresh context
      
      setIsEditing(false);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error(error);
      alert('Failed to update profile.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Delivered': return 'bg-green-100 text-green-800';
      case 'Dispatched': return 'bg-blue-100 text-blue-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-secondary">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-3xl font-serif font-bold text-primary mb-8">My Dashboard</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column: User Profile */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-100">
              <div className="px-4 py-5 sm:px-6 bg-primary">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg leading-6 font-medium text-white font-serif">Profile Details</h3>
                  <button onClick={handleLogout} className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 transition">
                    Sign Out
                  </button>
                </div>
              </div>
              <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
                <dl className="sm:divide-y sm:divide-gray-200">
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Full name</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {isEditing ? (
                        <input 
                          type="text" 
                          value={profileData.name}
                          onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                          className="border border-gray-300 rounded px-2 py-1 w-full"
                        />
                      ) : profileData.name}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Email</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user?.email}</dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Phone</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {isEditing ? (
                        <input 
                          type="text" 
                          value={profileData.phone}
                          onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                          className="border border-gray-300 rounded px-2 py-1 w-full"
                        />
                      ) : profileData.phone || 'N/A'}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Address</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {isEditing ? (
                        <input 
                          type="text" 
                          value={profileData.address}
                          onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                          className="border border-gray-300 rounded px-2 py-1 w-full"
                        />
                      ) : profileData.address || 'N/A'}
                    </dd>
                  </div>
                </dl>
              </div>
              <div className="bg-gray-50 px-4 py-4 sm:px-6 flex justify-between items-center">
                <button 
                  onClick={() => isEditing ? handleSaveProfile() : setIsEditing(true)}
                  className="text-sm font-medium text-accent hover:text-yellow-600"
                >
                  {isEditing ? 'Save Changes' : 'Edit Profile'}
                </button>
                <Link to="/change-password" className="text-sm font-medium text-gray-500 hover:text-primary">
                  Change Password
                </Link>
              </div>
            </div>
          </div>

          {/* Right Column: Order History */}
          <div className="lg:col-span-2">
            {/* Cart / Pending Items Section */}
            <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-100 mb-8">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center bg-accent text-white">
                <h3 className="text-lg leading-6 font-medium font-serif">Your Basket (Pending)</h3>
                <span className="text-sm bg-white text-accent px-2 py-0.5 rounded-full font-bold">{cart.length} Items</span>
              </div>
              <ul className="divide-y divide-gray-200">
                {cart.map((item) => (
                  <li key={item.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center">
                      <img src={item.imageURL} alt={item.name} className="h-12 w-12 object-cover rounded-md mr-4" />
                      <div>
                        <p className="text-sm font-medium text-primary">{item.name}</p>
                        <p className="text-xs text-gray-500">{item.quantity} x {item.unit}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900">₦{(item.price * item.quantity).toLocaleString()}</p>
                      <button onClick={() => removeFromCart(item.id)} className="text-xs text-red-600 hover:text-red-800 mt-1">Remove</button>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="bg-gray-50 px-4 py-4 sm:px-6 text-right border-t border-gray-200">
                <button 
                  onClick={() => navigate('/checkout')}
                  className="bg-primary text-white px-4 py-2 rounded text-sm font-medium hover:bg-green-800 transition"
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>

            {/* Order History Section */}
            <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-100">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-gray-200">
                <h3 className="text-lg leading-6 font-medium text-primary font-serif">Order History</h3>
                <span className="text-sm text-gray-500">Last 3 orders</span>
              </div>
              
              {loading ? (
                <div className="p-4 text-center text-gray-500">Loading orders...</div>
              ) : orders.length > 0 ? (
                <ul className="divide-y divide-gray-200">
                  {orders.slice(0, viewAllOrders ? orders.length : 3).map((order) => (
                    <li key={order.id} className="p-4 hover:bg-gray-50 transition">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <p className="text-sm font-medium text-primary truncate">
                            {order.id}
                          </p>
                          <p className="text-sm text-gray-500">
                            {order.items}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Placed on {order.date}
                          </p>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                          <p className="mt-2 text-sm font-bold text-gray-900">
                            ₦{order.total.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : <div className="p-4 text-center text-gray-500">No orders found.</div>}
              
              <div className="bg-gray-50 px-4 py-4 sm:px-6 text-right">
                <button 
                  onClick={() => setViewAllOrders(!viewAllOrders)}
                  className="text-sm font-medium text-primary hover:text-green-800"
                >
                  {viewAllOrders ? 'Show Less' : 'View all orders \u2192'}
                </button>
              </div>
            </div>
          </div>

        </div>

        {/* Available Products Section */}
        <div className="mt-16">
          <div className="flex justify-between items-end mb-6">
            <h2 className="text-2xl font-serif font-bold text-primary">Browse Our Produce</h2>
            <Link to="/" className="text-accent hover:text-yellow-600 font-medium text-sm">View Full Catalog &rarr;</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.slice(0, 4).map((product, index) => (
              <ProductCard key={index} product={product} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
